import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, BookOpen, ShoppingBag, Heart, History, RotateCcw, Smartphone } from "lucide-react";
import { bagById } from "../../lib/bags";
import { debugLog } from "../../lib/logger";


const getVisitorId = () => {
    let id = localStorage.getItem("bb_visitor_id");
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("bb_visitor_id", id);
    }
    return id;
};

const MOOD_CHIPS = [
    "Cozy & nostalgic",
    "Need a page-turner",
    "Something to make me cry",
    "Funny & light",
    "Mind-bending",
];

const MoodChips = ({ disabled, onPick }) => (
    <div className="mt-4 flex flex-wrap gap-2">
        {MOOD_CHIPS.map((chip) => (
            <button
                key={chip}
                data-testid={`mood-chip-${chip.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => onPick(chip)}
                disabled={disabled}
                className="px-3.5 py-1.5 rounded-full border border-white/20 text-xs text-[#FDF6EE]/70 hover:border-[#C4956A] hover:text-[#C4956A] transition-colors disabled:opacity-50"
            >
                {chip}
            </button>
        ))}
    </div>
);

const RecCard = ({ rec, index }) => {
    const bag = bagById(rec.bag_id);
    return (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-5 flex flex-col">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-[#C4956A]">
                <BookOpen size={12} /> {rec.genre || "Great read"}
            </span>
            <p className="font-serif text-xl mt-2 leading-snug">{rec.title}</p>
            <p className="text-xs text-[#FDF6EE]/50 mt-0.5">by {rec.author}</p>
            <p className="text-sm text-[#FDF6EE]/75 mt-3 leading-relaxed flex-1">
                {rec.reason}
            </p>
            {bag && (
                <Link
                    to={
                        rec.catalog_book_id
                            ? `/preorder?bag=${bag.id}&book=${rec.catalog_book_id}`
                            : `/preorder?bag=${bag.id}`
                    }
                    data-testid={`concierge-bag-link-${index}`}
                    className="mt-4 inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-[#C4956A]/15 border border-[#C4956A]/40 text-xs font-medium text-[#C4956A] hover:bg-[#C4956A] hover:text-white transition-colors"
                >
                    <ShoppingBag size={12} />
                    {rec.catalog_book_id
                        ? `Pre-order this book ${bag.emoji}`
                        : `Pre-order in ${bag.label} ${bag.emoji}`}
                </Link>
            )}
            {rec.bag_bestseller && (
                <p
                    data-testid={`concierge-bestseller-${index}`}
                    className="mt-2.5 text-[11px] text-[#FDF6EE]/50"
                >
                    <Heart size={10} className="inline text-[#E86A58] -mt-0.5 mr-1" fill="currentColor" />
                    Fans of this bag most pre-order{" "}
                    <em className="text-[#FDF6EE]/70">{rec.bag_bestseller.title}</em>
                </p>
            )}
        </div>
    );
};

const MoodForm = ({ mood, setMood, loading, onSubmit }) => (
    <form
        onSubmit={(e) => {
            e.preventDefault();
            if (mood.trim()) onSubmit(mood.trim());
        }}
        className="mt-6 flex flex-col sm:flex-row gap-3"
    >
        <input
            data-testid="concierge-mood-input"
            value={mood}
            maxLength={200}
            onChange={(e) => setMood(e.target.value)}
            placeholder="e.g. rainy Sunday, want something cozy…"
            className="flex-1 rounded-full bg-white/10 border border-white/20 px-5 py-3.5 text-[15px] text-[#FDF6EE] placeholder-[#FDF6EE]/40 focus:outline-none focus:ring-2 focus:ring-[#C4956A]/60 focus:border-[#C4956A]"
        />
        <button
            type="submit"
            disabled={loading || !mood.trim()}
            data-testid="concierge-submit-btn"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#C4956A] text-white font-medium hover:bg-[#A67C56] transition-colors disabled:opacity-50"
        >
            {loading ? <Loader2 className="animate-spin" size={17} /> : <Sparkles size={16} />}
            Find my book
        </button>
    </form>
);

const ConciergeIntro = () => (
    <>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#C4956A]">
            <Sparkles size={14} /> AI Book Concierge
        </span>
        <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">
            Tell us your mood,{" "}
            <span className="italic text-[#C4956A]">get a book</span>
        </h2>
        <p className="mt-3 text-[#FDF6EE]/70 max-w-xl text-sm md:text-base">
            Our concierge scans what readers are loving right now
            and hands you three picks that fit exactly how you feel.
        </p>
    </>
);

const ConciergeStatus = ({ err, loading }) => (
    <>
        {err && (
            <p data-testid="concierge-error" className="mt-5 text-sm text-[#E86A58]">
                {err}
            </p>
        )}
        {loading && (
            <p className="mt-6 text-sm text-[#FDF6EE]/60 animate-pulse">
                Flipping through what readers love right now…
            </p>
        )}
    </>
);

const MemoryBanner = ({ mood, loading, onReset, onRefresh }) => (
    <div
        data-testid="concierge-memory-banner"
        className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-[#FDF6EE]/70"
    >
        <span className="inline-flex items-center gap-1.5">
            <History size={14} className="text-[#C4956A]" />
            Welcome back! Here are your picks for{" "}
            <em className="text-[#C4956A] not-italic font-medium">“{mood}”</em>
        </span>
        <button
            onClick={onRefresh}
            disabled={loading}
            data-testid="concierge-fresh-picks-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#C4956A]/15 border border-[#C4956A]/40 text-xs font-medium text-[#C4956A] hover:bg-[#C4956A] hover:text-white transition-colors disabled:opacity-50"
        >
            <Sparkles size={11} /> Fresh picks, same mood
        </button>
        <button
            onClick={onReset}
            data-testid="concierge-memory-reset-btn"
            className="inline-flex items-center gap-1 text-xs text-[#FDF6EE]/50 hover:text-[#C4956A] underline underline-offset-2 transition-colors"
        >
            <RotateCcw size={11} /> Ask something new
        </button>
    </div>
);

const AppNote = () => (
    <p
        data-testid="concierge-app-note"
        className="mt-6 text-sm text-[#FDF6EE]/60 flex items-start gap-2"
    >
        <Smartphone size={15} className="text-[#C4956A] flex-shrink-0 mt-0.5" />
        <span>
            Your picks don't stop here — the{" "}
            <span className="text-[#FDF6EE]/85">BookBlend app</span> remembers them too,
            where the real conversation happens: chat with the concierge, track your reading,
            and swap recs with other readers.{" "}
            <a
                href="#download"
                data-testid="concierge-app-note-link"
                className="text-[#C4956A] underline underline-offset-2 hover:text-[#FDF6EE] transition-colors"
            >
                Get the app
            </a>
        </span>
    </p>
);

const requestRecs = async (moodText) => {
    const api = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${api}/api/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mood: moodText, visitor_id: getVisitorId() }),
    });
    if (res.status === 429)
        throw new Error("Whoa, bookworm! Give it a few minutes and try again.");
    if (!res.ok) throw new Error("The concierge stepped away — try again in a moment.");
    return (await res.json()).recommendations;
};

const useConciergeMemory = (setRecs, setMemoryMood) => {
    useEffect(() => {
        const controller = new AbortController();
        const loadMemory = async () => {
            try {
                const api = process.env.REACT_APP_BACKEND_URL;
                const res = await fetch(
                    `${api}/api/concierge/memory?visitor_id=${getVisitorId()}`,
                    { signal: controller.signal }
                );
                if (!res.ok) return;
                const data = await res.json();
                if (data.memory?.recommendations?.length) {
                    setRecs(data.memory.recommendations);
                    setMemoryMood(data.memory.mood);
                }
            } catch (e) {
                if (e.name !== "AbortError") debugLog("Concierge memory unavailable:", e);
            }
        };
        loadMemory();
        return () => controller.abort();
    }, [setRecs, setMemoryMood]);
};

const useConcierge = () => {
    const [mood, setMood] = useState("");
    const [recs, setRecs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [memoryMood, setMemoryMood] = useState(null);

    useConciergeMemory(setRecs, setMemoryMood);

    const ask = async (moodText) => {
        setErr("");
        setLoading(true);
        setRecs(null);
        setMemoryMood(null);
        try {
            setRecs(await requestRecs(moodText));
        } catch (e) {
            setErr(e.message);
        } finally {
            setLoading(false);
        }
    };

    const resetMemory = () => {
        setRecs(null);
        setMemoryMood(null);
        setMood("");
    };

    const pickChip = (chip) => {
        setMood(chip);
        ask(chip);
    };

    const refresh = () => {
        setMood(memoryMood);
        ask(memoryMood);
    };

    return { mood, setMood, recs, loading, err, memoryMood, ask, resetMemory, pickChip, refresh };
};

const ConciergeResults = ({ recs }) => (
    <>
        <div data-testid="concierge-results" className="mt-8 grid md:grid-cols-3 gap-4">
            {recs.map((r, i) => (
                <RecCard key={r.title} rec={r} index={i} />
            ))}
        </div>
        <AppNote />
    </>
);

export const Concierge = () => {
    const c = useConcierge();
    return (
        <section data-testid="concierge-section" className="py-20 px-6 sm:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="rounded-[2rem] bg-[#2D2D2D] text-[#FDF6EE] p-8 md:p-12">
                    <ConciergeIntro />
                    <MoodForm mood={c.mood} setMood={c.setMood} loading={c.loading} onSubmit={c.ask} />
                    <MoodChips disabled={c.loading} onPick={c.pickChip} />
                    <ConciergeStatus err={c.err} loading={c.loading} />
                    {c.memoryMood && c.recs && (
                        <MemoryBanner
                            mood={c.memoryMood}
                            loading={c.loading}
                            onReset={c.resetMemory}
                            onRefresh={c.refresh}
                        />
                    )}
                    {c.recs && <ConciergeResults recs={c.recs} />}
                </div>
            </div>
        </section>
    );
};
